import { useState, useCallback, useEffect, useMemo } from "react";
import { Building2, Wallet, CreditCard, Edit } from "lucide-react";
import styles from "./Accounts.module.css";
import AddAccountModal from "../../../components/Finance/Accounts/AddAccountModal";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import { getAccounts, createAccount, updateAccount } from "../../../api/finance/accounts";
import { useToast } from "../../../context/ToastContext";

const PAYMENT_METHOD_TO_API = {
  'Bank Transfer': 'bank_transfer',
  'SEPA': 'sepa',
  'SWIFT': 'swift',
  'Direct Debit': 'direct_debit',
  'Cash': 'cash',
  'Visa': 'visa',
  'Mastercard': 'mastercard',
  'Amex': 'amex',
  'Stripe': 'stripe',
  'PayPal': 'paypal',
  'Payment Terminal': 'payment_terminal',
};

const PAYMENT_METHOD_TO_LABEL = Object.fromEntries(
  Object.entries(PAYMENT_METHOD_TO_API).map(([label, val]) => [val, label])
);

const normalizeAccount = (acc) => ({
  id: acc.id,
  name: acc.account_name,
  type: acc.account_type === 'bank_account' ? 'bank' : acc.account_type === 'card_processing' ? 'card' : 'cash',
  accountNumber: acc.account_number || '',
  currency: acc.currency,
  balance: parseFloat(acc.balance) || 0,
  accountUsage: acc.account_usage || 'both',
  active: Boolean(acc.active),
  lastTransaction: acc.last_transaction || '',
  linkedPaymentMethods: (acc.linked_payment_methods || []).map(m => PAYMENT_METHOD_TO_LABEL[m] || m),
});

const toApiPayload = (form) => ({
  account_name: form.name,
  account_type: form.type === 'bank' ? 'bank_account' : form.type === 'card' ? 'card_processing' : 'cash',
  account_number: form.accountNumber,
  currency: form.currency,
  account_usage: form.accountUsage,
  active: form.active,
  linked_payment_methods: (form.linkedPaymentMethods || []).map(m => PAYMENT_METHOD_TO_API[m] || m),
});

const Accounts = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [usageFilter, setUsageFilter] = useState("");

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedRow, setSelectedRow] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccounts({ limit: 200 });
      setAccounts((res.data || []).map(normalizeAccount));
    } catch {
      setError("Failed to load accounts");
      showToast("Failed to load accounts", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'bank': return <Building2 size={18} />;
      case 'cash': return <Wallet size={18} />;
      case 'card': return <CreditCard size={18} />;
      default: return <Wallet size={18} />;
    }
  };

  const getAccountTypeName = (type) => {
    switch (type) {
      case 'bank': return 'Bank Account';
      case 'cash': return 'Cash';
      case 'card': return 'Card Processing';
      default: return type;
    }
  };

  const filteredRows = useMemo(() => {
    return accounts.filter((row) => {
      const matchesSearch = search === "" ||
        row.name.toLowerCase().includes(search.toLowerCase()) ||
        row.accountNumber.toLowerCase().includes(search.toLowerCase());

      const matchesStatus = statusFilter === "" ||
        (statusFilter === "true" && row.active) ||
        (statusFilter === "false" && !row.active);

      const matchesType = typeFilter === "" || row.type === typeFilter;
      const matchesUsage = usageFilter === "" || row.accountUsage === usageFilter;

      return matchesSearch && matchesStatus && matchesType && matchesUsage;
    });
  }, [accounts, search, statusFilter, typeFilter, usageFilter]);

  // Optimistic toggle: flip locally first, call API, revert on error
  const handleToggleActive = async (row) => {
    const currentActive = row.active === true || row.active === 1;
    const newValue = !currentActive;
    setAccounts((prev) =>
      prev.map((a) => (a.id === row.id ? { ...a, active: newValue } : a))
    );
    try {
      await updateAccount(row.id, { active: newValue });
      showToast(`Account ${newValue ? 'activated' : 'deactivated'}`, "success");
    } catch {
      setAccounts((prev) =>
        prev.map((a) => (a.id === row.id ? { ...a, active: currentActive } : a))
      );
      showToast("Failed to update account status", "error");
    }
  };

  // Instant-save the Active toggle from inside the Edit modal (same behaviour as
  // the list toggle). Keeps the list row AND the open modal's selectedRow in sync,
  // and reverts both on error.
  const persistActive = async (id, newValue) => {
    const prevValue = !newValue;
    setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, active: newValue } : a)));
    setSelectedRow((prev) => (prev && prev.id === id ? { ...prev, active: newValue } : prev));
    try {
      await updateAccount(id, { active: newValue });
      showToast(`Account ${newValue ? 'activated' : 'deactivated'}`, "success");
    } catch {
      setAccounts((prev) => prev.map((a) => (a.id === id ? { ...a, active: prevValue } : a)));
      setSelectedRow((prev) => (prev && prev.id === id ? { ...prev, active: prevValue } : prev));
      showToast("Failed to update account status", "error");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleAdd = () => {
    setMode("add");
    setSelectedRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setShowModal(true);
  };

  const handleSave = async (form) => {
    setSaveLoading(true);
    try {
      const payload = toApiPayload(form);
      if (mode === "add") {
        await createAccount(payload);
        showToast("Account created successfully", "success");
      } else if (selectedRow?.id) {
        await updateAccount(selectedRow.id, payload);
        showToast("Account updated successfully", "success");
      }
      setShowModal(false);
      await fetchAccounts();
    } catch (err) {
      showToast(err.message || "Failed to save account", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Accounts</div>
        <button className={styles.add} onClick={handleAdd}>+ Add Account</button>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon} aria-hidden>⚠</span>
        <span>Account settings affect payment processing, financial reporting, and transaction tracking.</span>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by account name or number..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>Search</button>
        </form>
        <SelectBox
          placeholder="Type: All"
          value={typeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "Bank Account", value: "bank" },
            { label: "Cash", value: "cash" },
            { label: "Card Processing", value: "card" },
          ]}
          onChange={(e) => setTypeFilter(e.target.value)}
        />
        <SelectBox
          placeholder="Usage: All"
          value={usageFilter}
          options={[
            { label: "All Usage", value: "" },
            { label: "Client Payments", value: "client" },
            { label: "Supplier Payments", value: "supplier" },
            { label: "Both", value: "both" },
          ]}
          onChange={(e) => setUsageFilter(e.target.value)}
        />
        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "true" },
            { label: "Inactive", value: "false" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading accounts...</div>
      ) : (
        <div className={styles.tableWrap}>
          <div className={styles.meta}>
            Showing {filteredRows.length} of {accounts.length} accounts
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ACCOUNT NAME</th>
                <th>TYPE</th>
                <th>ACCOUNT NUMBER</th>
                <th>CURRENCY</th>
                <th>BALANCE</th>
                <th>USAGE</th>
                <th>PAYMENT METHODS</th>
                <th>LAST TRANSACTION</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                    No accounts found
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.accountName}>
                        <div className={styles.accountIcon}>{getAccountTypeIcon(row.type)}</div>
                        {row.name}
                      </div>
                    </td>
                    <td><span className={styles.badge}>{getAccountTypeName(row.type)}</span></td>
                    <td className={styles.accountNumber}>{row.accountNumber}</td>
                    <td className={styles.currency}>{row.currency}</td>
                    <td className={styles.balance}>
                      €{row.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>
                      <span className={styles.badge}>
                        {row.accountUsage === 'client' ? 'Client' : row.accountUsage === 'supplier' ? 'Supplier' : 'Both'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.methods}>
                        {(row.linkedPaymentMethods || []).slice(0, 2).map((method, idx) => (
                          <span key={idx} className={styles.methodTag}>{method}</span>
                        ))}
                        {(row.linkedPaymentMethods || []).length > 2 && (
                          <span className={styles.methodTag}>+{row.linkedPaymentMethods.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.date}>{row.lastTransaction || '—'}</td>
                    <td>
                      <ToggleSwitch
                        checked={row.active}
                        onChange={() => handleToggleActive(row)}
                      />
                    </td>
                    <td>
                      <button className={styles.editBtn} onClick={() => handleEdit(row)} title="Edit account">
                        <Edit size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <AddAccountModal
        open={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        initialData={selectedRow}
        onSave={handleSave}
        onActiveToggle={persistActive}
        saveLoading={saveLoading}
      />
    </div>
  );
};

export default Accounts;
